import {
  Children,
  ComponentProps,
  isValidElement,
  ReactElement,
  ReactNode,
} from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

const ICON_SIZES = ['icon', 'icon-sm', 'icon-lg'] as const;

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-transparent text-sm font-medium transition-colors shrink-0 outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80',
        outline:
          'bg-button-outline text-button-outline-foreground shadow-xs hover:bg-button-outline-hover hover:text-button-outline-hover-foreground active:bg-button-outline-active data-[state=open]:bg-button-outline-hover',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/70 active:bg-secondary/50',
        ghost:
          'text-foreground hover:bg-accent active:bg-muted data-[state=open]:bg-accent',
        'ghost-destructive':
          'text-destructive-soft-foreground hover:bg-destructive-soft active:bg-destructive-soft',
        link: 'h-auto! border-0 px-0! text-link underline-offset-4 hover:underline',
        inverse:
          'bg-secondary text-secondary-foreground hover:bg-secondary/70 active:bg-secondary/50',
        success:
          'bg-button-success text-button-success-foreground hover:ring-1 hover:ring-button-success-foreground hover:ring-inset active:ring-1 active:ring-button-success-foreground active:ring-inset active:bg-button-success-active active:text-button-success-active-foreground',
        field: '',
        warning:
          'bg-warning-soft text-warning-soft-foreground hover:bg-warning-soft/70',
      },
      size: {
        default: 'h-8 px-3 data-icon-only:has-[>svg]:px-2',
        xs: "h-6 gap-1 px-2 text-xs data-icon-only:has-[>svg]:px-1 [&_svg:not([class*='size-'])]:size-3.5",
        sm: 'h-7 px-2.5 data-icon-only:has-[>svg]:px-1.5',
        lg: 'h-9 gap-2 px-4 data-icon-only:has-[>svg]:px-2.5',
        icon: 'size-8',
        'icon-sm': 'size-7',
        'icon-lg': 'size-9',
      },
    },
    compoundVariants: [
      {
        variant: 'outline',
        size: [...ICON_SIZES],
        className:
          'border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground active:bg-primary-soft',
      },
      {
        variant: 'ghost',
        size: [...ICON_SIZES],
        className: 'text-muted-foreground hover:text-foreground',
      },
      {
        variant: 'destructive',
        size: [...ICON_SIZES],
        className:
          'bg-transparent text-destructive-soft-foreground hover:bg-destructive-soft active:bg-destructive-soft',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const FIELD_BUTTON_CLASS_NAME =
  "inline-flex w-full shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap font-normal transition-[color,box-shadow,border-color] outline-none disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

const Button = (
  props: ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
      isLoading?: boolean;
    },
) => {
  const {
    className,
    variant = 'default',
    size = 'default',
    asChild = false,
    isLoading = false,
    disabled,
    children,
    ...restProps
  } = props;
  const Comp = asChild ? Slot : 'button';
  const content =
    asChild && isValidElement(children)
      ? (children as ReactElement<{ children?: ReactNode }>).props.children
      : children;
  const childNodes = Children.toArray(content);
  const isIconOnly = childNodes.length === 1 && isValidElement(childNodes[0]);
  const classNames =
    variant === 'field'
      ? cn(FIELD_BUTTON_CLASS_NAME, className)
      : cn(buttonVariants({ variant, size, className }));

  // When asChild is true, we can't use isLoading (would need to wrap in extra element)
  // and we shouldn't pass button-specific data attributes
  if (asChild) {
    return (
      <Comp
        data-icon-only={isIconOnly || undefined}
        className={classNames}
        {...restProps}
      >
        {children}
      </Comp>
    );
  }

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-icon-only={isIconOnly || undefined}
      className={classNames}
      disabled={disabled || isLoading}
      {...restProps}
    >
      {isLoading && <Loader2 className="animate-spin" />}
      {children}
    </Comp>
  );
};

export { Button, buttonVariants };
